import React, { useEffect, useState, useCallback, useRef } from "react";
import Topbar from "../Navigations/Topbar";
import Footer from "../../Footer";
import { fetchAllCustomerProduct } from "../../../service/ProductService";
import { useToast } from "../../toast/ToastProvider";
import { fetchProductImage } from "../../../service/ProductImageService";
import { apiUrl } from "../../../config";
import { Link } from "react-router-dom";
import { fetchCategory } from "../../../service/CategoryService";
import { formatINR } from "../../../App";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  createCart,
  getCartProductsByCustomerId,
} from "../../../service/CartService";
// Import wishlist services
import {
  createWishlist,
  deleteWishlistById,
  getWishlistProductsByCustomerId,
} from "../../../service/WishlistService";
import ProductCard from "../Card/ProductCard";
import ReactSlider from "react-slider";
import { ArrowDown, ArrowUp } from "react-feather";
import "../../../styles/topbar.css";

const Products = () => {
  const [price, setPrice] = useState([0, 0]);
  const [globalMin, setGlobalMin] = useState(0);
  const [globalMax, setGlobalMax] = useState(0);
  const [isPriceFilterOpen, setIsPriceFilterOpen] = useState(false);
  const [sorted, setSorted] = useState(null);
  const [productData, setProductData] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const toast = useToast();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const categoryId = params.get("categoryId");
  const hasAutoAppliedRef = useRef(false);
  const navigate = useNavigate();
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const authUser = useSelector((state) => state.auth.authUser);

  // ✅ Debounce ref for price slider (optional UX improvement)
  const priceDebounceRef = useRef(null);

  // ✅ Helper function to calculate final price with discount (consistent logic)
  const calculateFinalPrice = (product) => {
    const price = Number(product.ornamentPrice);

    if (isNaN(price)) return 0;
    if (product.discount > 0) {
      return (
        product.ornamentPrice -
        (product.priceBeforeGst * product.discount) / 100
      );
    }
    return product.ornamentPrice || 0;
  };

  const fetchData = async () => {
    try {
      const products = await fetchAllCustomerProduct();
      const images = await fetchProductImage();

      const merged = products?.map((product) => {
        const productImages = images?.filter(
          (img) => img.productId === product.id,
        );
        return { ...product, images: productImages };
      });

      // ✅ Calculate prices with discount applied (consistent with filter logic)
      // const prices = merged.map((p) => calculateFinalPrice(p));
      const prices = merged
        .map((p) => calculateFinalPrice(p))
        .filter((p) => p > 0);

      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      setGlobalMin(minPrice);
      setGlobalMax(maxPrice);
      setPrice([minPrice, maxPrice]);

      setProductData(merged);
      setFilteredProducts(merged);
    } catch (error) {
      toast("Failed to fetch products or images", "ERROR");
    }
  };

  // ✅ FIXED: Sorting now uses calculateFinalPrice for consistency
  const sortAscending = () => {
    const sorted = [...filteredProducts].sort((a, b) => {
      const priceA = calculateFinalPrice(a);
      const priceB = calculateFinalPrice(b);
      return priceA - priceB;
    });
    setFilteredProducts(sorted);
    setSorted("asc");
  };

  const sortDescending = () => {
    const sorted = [...filteredProducts].sort((a, b) => {
      const priceA = calculateFinalPrice(a);
      const priceB = calculateFinalPrice(b);
      return priceB - priceA;
    });
    setFilteredProducts(sorted);
    setSorted("desc");
  };

  // ✅ FIXED: Main filter effect - applies category (URL priority) AND price filters consistently
  useEffect(() => {
    if (productData.length === 0) return;

    let filtered = [...productData];

    // ✅ URL categoryId takes priority over manual selection
    let activeCategories = [];
    if (categoryId) {
      activeCategories = [Number(categoryId)];
    } else if (selectedCategories.length > 0) {
      activeCategories = selectedCategories;
    }

    // Apply category filter
    if (activeCategories.length > 0) {
      filtered = filtered.filter((product) =>
        activeCategories.includes(product.categoryId),
      );
    }

    // Apply price filter ONLY if not at full range
    if (price[0] !== globalMin || price[1] !== globalMax) {
      filtered = filtered.filter((product) => {
        const finalPrice = calculateFinalPrice(product);
        return finalPrice >= price[0] && finalPrice <= price[1];
      });
    }

    setFilteredProducts(filtered);
  }, [
    price,
    selectedCategories,
    productData,
    globalMin,
    globalMax,
    categoryId,
  ]);

  // ✅ FIXED: Category ID from URL effect - auto-applies category filter when URL changes
  useEffect(() => {
    if (!categoryId || productData.length === 0) return;

    const catId = Number(categoryId);

    // Update selected categories to match URL
    setSelectedCategories([catId]);
    hasAutoAppliedRef.current = true;

    // Filter products by category
    const filtered = productData.filter(
      (product) => product.categoryId === catId,
    );

    // Recalculate price range for filtered products
    if (filtered.length > 0) {
      const prices = filtered.map((p) => calculateFinalPrice(p));
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      setGlobalMin(minPrice);
      setGlobalMax(maxPrice);
      setPrice([minPrice, maxPrice]);
    } else {
      // Reset price range if no products found
      setGlobalMin(0);
      setGlobalMax(0);
      setPrice([0, 0]);
    }

    setFilteredProducts(filtered);
  }, [categoryId, productData]);

  useEffect(() => {
    if (categoryId === null && hasAutoAppliedRef.current) {
      setSelectedCategories([]);
      hasAutoAppliedRef.current = false;
      // Reset price to global range
      setPrice([globalMin, globalMax]);
    }
  }, [categoryId, globalMin, globalMax]);

  const fetchCategories = async () => {
    try {
      const res = await fetchCategory();
      setCategories(res);
    } catch (error) {
      toast("Error in fetching categories", "ERROR");
    }
  };

  // Fetch wishlist data
  const fetchWishlistData = async () => {
    if (!authUser?.id) return;
    try {
      const response = await getWishlistProductsByCustomerId(authUser.id);
      setWishlist(response || []);
    } catch (error) {
      console.error("Failed to fetch wishlist:", error);
      toast("Failed to load wishlist", "ERROR");
    }
  };

  // ✅ FIXED: handleCategoryChange for manual checkbox selection
  const handleCategoryChange = useCallback(
    (e, id) => {
      const checked = e.target.checked;

      setSelectedCategories((prevSelected) => {
        let updatedCategories;
        if (checked) {
          updatedCategories = [...prevSelected, id];
        } else {
          updatedCategories = prevSelected.filter((catId) => catId !== id);
        }

        // Remove categoryId from URL if no categories selected manually
        if (updatedCategories.length === 0 && categoryId) {
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete("categoryId");
          navigate({ search: searchParams.toString() }, { replace: true });
        }

        return updatedCategories;
      });
    },
    [categoryId, location.search, navigate],
  );

  const isProductInCart = (productId, customerId) => {
    return cart.some(
      (item) => item.productId === productId && item.customerId === customerId,
    );
  };

  const isProductInWishlist = (productId) => {
    return wishlist.some((item) => item.productId === productId);
  };

  const fetchCartData = async () => {
    if (authUser?.id) {
      try {
        const cartData = await getCartProductsByCustomerId(authUser.id);
        setCart(cartData);
      } catch (error) {
        console.error("Failed to fetch cart:", error);
        toast("Failed to load cart data", "ERROR");
      }
    }
  };

  const addToCart = async (productId) => {
    if (authUser === null) {
      navigate("/login");
      return;
    }

    try {
      await createCart({ customerId: authUser.id, productId, quantity: 1 });
      toast("Product added to cart", "SUCCESS");

      // Refresh cart data to update button states
      const cartData = await getCartProductsByCustomerId(authUser.id);
      setCart(cartData);
    } catch (error) {
      toast("Failed to add product", "ERROR");
    }
  };

  // Wishlist functionality
  const toggleWishlist = async (productId) => {
    if (authUser === null) {
      navigate("/login");
      return;
    }

    try {
      if (isProductInWishlist(productId)) {
        // Remove from wishlist
        const wishlistItem = wishlist.find(
          (item) => item.productId === productId,
        );
        if (wishlistItem) {
          await deleteWishlistById(wishlistItem.wishlistId);
          setWishlist((prev) =>
            prev.filter((item) => item.productId !== productId),
          );
          toast("Removed from wishlist", "SUCCESS");
        }
      } else {
        // Add to wishlist
        await createWishlist({ customerId: authUser.id, productId });
        const updatedWishlist = await getWishlistProductsByCustomerId(
          authUser.id,
        );
        setWishlist(updatedWishlist);
        toast("Added to wishlist", "SUCCESS");
      }
    } catch (error) {
      toast("Failed to update wishlist", "ERROR");
    }
  };

  const handleGoToCart = () => {
    navigate("/shopping-cart");
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
    fetchCategories();
    fetchCartData();
    fetchWishlistData();
  }, [authUser?.id]);

  // ✅ Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (priceDebounceRef.current) {
        clearTimeout(priceDebounceRef.current);
      }
    };
  }, []);

  // ✅ Debounced price handler for smoother slider UX (optional)
  const handlePriceChange = (newPrice) => {
    if (priceDebounceRef.current) {
      clearTimeout(priceDebounceRef.current);
    }
    priceDebounceRef.current = setTimeout(() => {
      setPrice(newPrice);
    }, 300);
  };

  const styles = {
    productItem: {
      listStyle: "none",
      marginBottom: "30px",
    },
    productname: {
      fontFamily: "Raleway, sans-serif",
    },
    productInner: {
      background: "#fff",
      borderRadius: "16px",
      overflow: "hidden",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
      position: "relative",
      height: "100%",
    },
    flash: {
      background: "#c05c2eff",
      color: "#fff",
      fontSize: "12px",
      fontWeight: 600,
      textTransform: "uppercase",
      padding: "4px 10px",
      borderRadius: "12px",
      position: "absolute",
      top: "12px",
      left: "12px",
      zIndex: 2,
    },
    heartBtn: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontSize: "20px",
      position: "absolute",
      top: "12px",
      right: "12px",
      zIndex: 2,
    },
    productThumb: {
      position: "relative",
      textAlign: "center",
      height: "250px",
      overflow: "hidden",
    },
    productImg: {
      width: "100%",
      height: "100%",
      objectFit: "fill",
      transition: "transform 0.3s ease",
    },
    productInfo: {
      padding: "15px",
      textAlign: "center",
    },
    productName: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#222",
      textDecoration: "none",
      fontFamily: "Raleway, sans-serif",
    },
  };

  const selectedCategoryName = categoryId
    ? categories.find((cat) => cat.id === Number(categoryId))?.name
    : "Products";
  console.log("sele", selectedCategoryName);

  return (
    <div>
      <header className="header style7">
        <Topbar />
      </header>
      <div className="main-content main-content-product left-sidebar">
        <div className="container">
          <div className="row pt-4 pb-2">
            <div className="col-lg-12">
              <div className="breadcrumb-trail breadcrumbs">
                <ul className="trail-items breadcrumb">
                  <li className="trail-item trail-begin">
                    <Link to="/" className="text-decoration-none">
                      Home
                    </Link>
                  </li>
                  <li className="trail-item trail-end active">Products</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="content-area shop-grid-content no-banner">
              <h3 className="py-3" style={{ textTransform: "capitalize" }}>
                {selectedCategoryName}
              </h3>

              <div style={{ marginBottom: "20px", borderRadius: "12px" }}>
                <div
                  style={{
                    background: "lightgray",
                    padding: "5px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    onClick={() => setIsPriceFilterOpen(!isPriceFilterOpen)}
                    style={{
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      backgroundColor: "#fff",
                      color: "black",
                      fontSize: "16px",
                      cursor: "pointer",
                      textAlign: "left",
                      width: "50%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    className="m-3"
                  >
                    <span>Filter by Price</span>
                    <span>{isPriceFilterOpen ? "▲" : "▼"}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      marginBottom: "5px",
                      marginRight: "10px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "20px",
                        cursor: "pointer",
                        color: sorted === "asc" ? "blue" : "black",
                      }}
                      onClick={sortAscending}
                      title="Sort Ascending"
                    >
                      <ArrowUp />
                    </span>

                    <span
                      style={{
                        fontSize: "20px",
                        cursor: "pointer",
                        color: sorted === "desc" ? "blue" : "black",
                      }}
                      onClick={sortDescending}
                      title="Sort Descending"
                    >
                      <ArrowDown />
                    </span>
                  </div>
                </div>

                {isPriceFilterOpen && (
                  <div
                    className="price-filter-box"
                    style={{
                      padding: "20px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "0 0 8px 8px",
                      width: "50%",
                      overflowY: "auto",
                      transition: "max-height 0.3s ease-in-out",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          color: "#A38560",
                          fontSize: "15px",
                          fontWeight: "bold",
                          minWidth: "20px",
                        }}
                      >
                        ₹{Math.floor(price[0])}
                      </span>

                      <ReactSlider
                        value={price}
                        onChange={handlePriceChange} // ✅ Using debounced handler
                        min={globalMin}
                        max={globalMax}
                        step={1}
                        className="dual-range-slider"
                        thumbClassName="slider-thumb"
                        // trackClassName="slider-track"
                        renderTrack={(props, state) => (
                          <div
                            {...props}
                            className={`slider-track slider-track-${state.index}`}
                          />
                        )}
                      />

                      <span
                        style={{
                          color: "#A38560",
                          fontWeight: "bold",
                          fontSize: "15px",
                          minWidth: "20px",
                          textAlign: "center",
                        }}
                      >
                        ₹{Math.ceil(price[1])}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {filteredProducts.length === 0 ? (
                <div
                  className="text-center py-5"
                  style={{
                    borderRadius: "20px",
                    padding: "50px 20px",
                    color: "black",
                    fontSize: "26px",
                    fontWeight: "700",
                    letterSpacing: "1px",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
                  }}
                >
                  ✨ This product is coming soon! Stay tuned for our stunning
                  new{" "}
                  <span style={{ textTransform: "capitalize" }}>
                    {selectedCategoryName}
                  </span>{" "}
                  collection 💎
                </div>
              ) : (
                <ul className="row list-products auto-clear equal-container product-grid">
                  {filteredProducts?.map((data, index) => {
                    console.log("fi", filteredProducts);

                    const productExistInCart = isProductInCart(
                      data.id,
                      authUser?.id,
                    );
                    const productInWishlist = isProductInWishlist(data.id);
                    return (
                      <ProductCard
                        key={index}
                        data={data}
                        styles={styles}
                        inWishlist={productInWishlist}
                        inCart={productExistInCart}
                        onToggleWishlist={toggleWishlist}
                        onAddToCart={addToCart}
                        onGoToCart={handleGoToCart}
                        isFromWishlist={false}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <div className="footer-device-mobile">
        <div className="wapper">
          <div className="footer-device-mobile-item device-home">
            <Link to="/" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-house"></i>
              </span>{" "}
              Home
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-cart">
            <Link to="/wishlist" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-heart"></i>
                <span className="count-icon">{wishlist.length}</span>
              </span>{" "}
              Wishlist
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-cart">
            <Link to="/shopping-cart" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-shopping-cart-simple" />
                <span className="count-icon">{cart.length}</span>
              </span>{" "}
              Cart
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-user">
            <Link to="/profile" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-user"></i>
              </span>{" "}
              Account
            </Link>
          </div>
        </div>
      </div>
      <Link to="#" className="backtotop">
        <i
          className="ph ph-caret-double-up"
          style={{
            color: "white",
            fontSize: "30px",
          }}
        ></i>
      </Link>
    </div>
  );
};

export default Products;
